<template>
  <div class="columns">
    <div class="column">
      <h2 class="title is-2">
        Update payment details
      </h2>
      <br>
      <form class="form">
        <div class="columns">
          <div class="column is-half">
            <h3 class="title is-3">
              New billing details
            </h3>

            <div class="field">
              <div class="control">
                <input
                  v-model="billing_details.name"
                  class="input"
                  type="text"
                  required
                  placeholder="Cardholder name"
                >
              </div>
            </div>

            <div class="field">
              <div :id="cardElId" />

              <p
                v-if="cardError"
                id="card-errors"
                class="help is-danger"
                role="alert"
              >
                {{ cardError }}
              </p>
            </div>

            <figure>
              <img
                src="@/assets/card-brands.png"
                alt="Accepted card brands: Visa, Mastercard, American Express, Maestro."
              >
            </figure>
            <br>
          </div>

          <div
            v-if="currentCard"
            class="column"
          >
            <h3 class="title is-3">
              Existing billing details
            </h3>

            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">Card</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      disabled
                      :value="'*** *** **** ' + currentCard.last4"
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">Expiry</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      disabled
                      :value="(parseInt(currentCard.exp_month) < 10 ? '0' : '') + currentCard.exp_month + '/' + currentCard.exp_year"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="footer">
          <div class="buttons is-pulled-right">
            <router-link
              class="button is-secondary is-large is-pulled-right"
              :to="{ name: 'subscription' }"
            >
              Cancel
            </router-link>
            <button
              class="button is-primary is-large is-pulled-right"
              :class="{ 'is-loading': busy }"
              :disabled="!canSubmit || busy"
              @click.prevent="handleUpdateClick"
            >
              Update
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import paymentDetailsClient from '@/api/paymentDetails.js'

export default {
  name: 'LicenseSelectionModalItem',

  data () {
    return {
      currentCard: null,
      billing_details: {
        name: ''
      },
      promoCode: '',
      promoApplied: false,
      promoError: null,
      teamData: {
        name: '', // Don't change the name in this component. Needed for API though.
        billing_company_name: '',
        billing_contact: '',
        address_1: '',
        address_2: '',
        city: '',
        county: '',
        postal_code: '',
        country: ''
      },
      countryOptions: [],
      clientSecret: null,
      cardElId: 'card-element',
      cardElSelector: '#card-element',
      cardComplete: false,
      cardError: null,
      stripe: null,
      stripeElements: null,
      stripeCard: null,
      loadingInvoice: true,
      stripeInvoice: null,
      busy: false,
      checkoutComplete: false
    }
  },

  computed: {
    canSubmit: function () {
      return this.cardComplete && this.billing_details.name
    }
  },

  mounted () {
    this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    this.stripeElements = this.stripe.elements()

    // Custom styling can be passed to options when creating an Element.
    // (Note that this demo uses a wider set of styles than the guide below.)
    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }

    // Create an instance of the card Element.
    this.stripeCard = this.stripeElements.create('card', { style })
    this.stripeCard.on('change', (event) => {
      this.cardComplete = event.complete
    })
    this.stripeCard.mount(this.cardElSelector)

    paymentDetailsClient.retrieve()
      .then((response) => {
        this.currentCard = response.data
      })
  },

  methods: {
    handleUpdateClick: function () {
      const vm = this
      vm.stripe.createPaymentMethod({
        type: 'card',
        card: vm.stripeCard,
        billing_details: vm.billing_details
      })
        .then((createPaymentMethodResponse) => {
          if (createPaymentMethodResponse.hasOwnProperty('error')) {
            throw createPaymentMethodResponse
          }

          const paymentMethodId = createPaymentMethodResponse.paymentMethod.id
          paymentDetailsClient.update({ payment_method: paymentMethodId })
            .then((updateResponse) => {
              if (updateResponse.hasOwnProperty('error')) {
                throw updateResponse
              }

              vm.$router.push({ name: 'subscription', params: { showPaymentDetailsUpdated: true } })
            })
        })
        .catch(error => {
          // All-purpose error handler
          vm.busy = false
          if (error.hasOwnProperty('response')) { // Errors from backend API.
            vm.cardError = error.response.data.error.message
          }
          if (error.hasOwnProperty('error')) { // Errors from client-side Stripe interactions.
            vm.cardError = error.error.message
          }
        })
    }
  },

  metaInfo: {
    title: 'Update payment details'
  }
}
</script>

<style lang="scss" scoped>
.StripeElement {
  box-sizing: border-box;

  height: 40px;

  padding: 10px 12px;

  border: 1px solid transparent;
  border-radius: 4px;
  background-color: white;

  box-shadow: 0 1px 3px 0 #e6ebf1;
  -webkit-transition: box-shadow 150ms ease;
  transition: box-shadow 150ms ease;
}

.StripeElement--focus {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

.StripeElement--invalid {
  border-color: #fa755a;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}
</style>
